import React from "react";
import { durationToHuman, formatters, translate } from "@kaltura/mediaspace-shared-utils";
import styled from "@emotion/styled";
import { alpha, Box, StyledThumbnail as Thumbnail } from "@kaltura/mediaspace-shared-styled";
import { TruncateWithTooltip } from "@kaltura/mediaspace-shared-ui";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics, useEntryUrl } from "@kaltura/mediaspace-shared-hooks";
import { KalturaEntryType } from "kaltura-typescript-client/api/types/KalturaEntryType";
import { Link } from "@kaltura/ds-react-components";
import { VisibleFocusStyle } from "@kaltura/ds-react-theme";

export interface GridItemProps {
    name: string;
    image?: string;
    duration?: number;
    entryId: string;
    entryType: KalturaEntryType;
    categoryId?: number;
    playlistId?: string;
    analyticsName?: string;
}

const StyledGridItem = styled(Link)(({ theme }) => ({
    backgroundColor: theme.kaltura.palette.tone7,
    borderRadius: theme.kaltura.shape.roundness1,
    overflow: "hidden",
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.kaltura.typography.fontWeightBold,
    color: theme.kaltura.palette.tone1,
    textDecoration: "none",
    display: "block",
    "&:focus-visible": {
        ...VisibleFocusStyle,
    },
}));

const ThumbnailWrapper = styled(Box)(({ theme }) => ({
    position: "relative",
    borderTopLeftRadius: theme.kaltura.shape.roundness1,
    borderTopRightRadius: theme.kaltura.shape.roundness1,
}));

const StyledThumbnail = styled(Thumbnail)(({ theme, width, height }) => ({
    "&": {
        minWidth: width,
        overflow: "hidden",
        borderTopLeftRadius: theme.kaltura.shape.roundness1,
        borderTopRightRadius: theme.kaltura.shape.roundness1,
        width: "100%",
        aspectRatio: "16/9",
    },
}));

const StyledDuration = styled.span(({ theme }) => ({
    backgroundColor: alpha(theme.palette.common.black, 0.6),
    borderRadius: theme.kaltura.shape.roundness1,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: theme.typography.pxToRem(16),
    fontWeight: theme.kaltura.typography.fontWeightBold,
    padding: "4px 6px",
    left: theme.spacing(1),
    bottom: theme.spacing(1),
    position: "absolute",
    color: theme.palette.common.white,
}));

const StyledNameContainer = styled.div(({ theme }) => ({
    height: 36,
    padding: theme.spacing(2),
    boxSizing: "content-box",
}));

const baseClass = "kms-ds-media-grid-item";

/**
 * Grid item with accessibility using ARIA grid pattern.
 */
const GridItem = (props: GridItemProps) => {
    const { name, image = "", duration, entryId, entryType, categoryId, playlistId, analyticsName } = props;

    const linkUrl = useEntryUrl({
        entry: { id: entryId, name, type: entryType },
        categoryId,
        playlistId,
    });

    const sendButtonAnalytics = useButtonAnalytics();

    const sendAnalytics = () => {
        if (analyticsName) {
            sendButtonAnalytics(analyticsName, ButtonClickAnalyticsType.THUMBNAIL);
        }
    };

    return (
        <StyledGridItem
            aria-label={
                duration
                    ? translate("Go to %1 with duration: %2", [name, durationToHuman(duration)])
                    : translate("Go to %1", [name])
            }
            className={`${baseClass}`}
            onClick={sendAnalytics}
            href={linkUrl}
        >
            <ThumbnailWrapper className={`${baseClass}-thumbnail`}>
                <StyledThumbnail tabIndex={-1} src={image} aria-hidden={true} />
                {!!duration && (
                    <StyledDuration
                        aria-label={durationToHuman(duration)}
                        role={"text"}
                        className={`${baseClass}-duration`}
                    >
                        {formatters.formatSecondsToTime(duration)}
                    </StyledDuration>
                )}
            </ThumbnailWrapper>
            <StyledNameContainer className={`${baseClass}-content-container`}>
                <TruncateWithTooltip text={name} lines={2}>
                    <span className={`${baseClass}-content`} id={`item-title-${entryId}`}>
                        {name}
                    </span>
                </TruncateWithTooltip>
            </StyledNameContainer>
        </StyledGridItem>
    );
};

export default GridItem;
